import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { ArrowLeft, GitCompare, X, Car as CarIcon } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useCompare, MAX_COMPARE } from "@/hooks/use-compare";
import { buildSeoMeta } from "@/lib/seo";
import { resolveCarSlug } from "@/lib/car-slug";

const searchSchema = z.object({
  ids: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/sammenlign")({
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    ...buildSeoMeta({
      title: "Sammenlign biler · AutoXChange",
      description:
        "Sammenlign op til 4 biler side om side på pris, årgang, kilometer, brændstof, gear og udstyr.",
      path: "/sammenlign",
    }),
  }),
  component: ComparePage,
});

interface CompareCar {
  id: string;
  brand: string;
  model: string;
  variant: string | null;
  year: number;
  mileage: number;
  price: number;
  fuel_type: string | null;
  transmission: string | null;
  body_type: string | null;
  color: string | null;
  doors: number | null;
  seats: number | null;
  engine_size: string | null;
  horsepower: number | null;
  first_registration: string | null;
  is_leasing: boolean | null;
  leasing_price: number | null;
  status: string;
  images: string[] | null;
  equipment: string[] | null;
  seo_slug: string | null;
}

const fmtNumber = (n: number | null | undefined) =>
  n == null ? "—" : new Intl.NumberFormat("da-DK").format(n);

const fmtPrice = (n: number | null | undefined) =>
  n == null ? "—" : `${new Intl.NumberFormat("da-DK").format(n)} kr`;

const fuelLabel = (f: string | null) => {
  if (!f) return "—";
  const map: Record<string, string> = {
    benzin: "Benzin",
    diesel: "Diesel",
    el: "El",
    hybrid: "Hybrid",
    plugin_hybrid: "Plugin Hybrid",
  };
  return map[f] ?? f;
};

const txtOrDash = (s: string | null | undefined) =>
  s && s.length > 0 ? s : "—";

interface SpecRow {
  label: string;
  values: Array<string | number | null | undefined>;
  highlight?: "min" | "max" | null;
}

function buildRows(cars: CompareCar[]): SpecRow[] {
  return [
    {
      label: "Pris",
      values: cars.map((c) => fmtPrice(c.price)),
      highlight: "min",
    },
    {
      label: "Leasing/md",
      values: cars.map((c) =>
        c.is_leasing && c.leasing_price ? `${fmtNumber(c.leasing_price)} kr` : "—",
      ),
    },
    { label: "Årgang", values: cars.map((c) => c.year), highlight: "max" },
    {
      label: "Kilometer",
      values: cars.map((c) => `${fmtNumber(c.mileage)} km`),
      highlight: "min",
    },
    { label: "Brændstof", values: cars.map((c) => fuelLabel(c.fuel_type)) },
    { label: "Gear", values: cars.map((c) => txtOrDash(c.transmission)) },
    { label: "Karrosseri", values: cars.map((c) => txtOrDash(c.body_type)) },
    { label: "Farve", values: cars.map((c) => txtOrDash(c.color)) },
    {
      label: "Hestekræfter",
      values: cars.map((c) => (c.horsepower ? `${c.horsepower} hk` : "—")),
      highlight: "max",
    },
    { label: "Motorstørrelse", values: cars.map((c) => txtOrDash(c.engine_size)) },
    { label: "Døre", values: cars.map((c) => c.doors ?? "—") },
    { label: "Sæder", values: cars.map((c) => c.seats ?? "—") },
    {
      label: "1. registrering",
      values: cars.map((c) => txtOrDash(c.first_registration)),
    },
    {
      label: "Status",
      values: cars.map((c) =>
        c.status === "active"
          ? "Til salg"
          : c.status === "sold"
            ? "Solgt"
            : c.status,
      ),
    },
  ];
}

function ComparePage() {
  const { ids: searchIds } = Route.useSearch();
  const { remove, clear } = useCompare();
  const [cars, setCars] = useState<CompareCar[] | null>(null);

  const idList: string[] = (searchIds as string)
    .split(",")
    .map((s: string) => s.trim())
    .filter((s: string) => s.length > 0)
    .slice(0, MAX_COMPARE);

  useEffect(() => {
    if (idList.length === 0) {
      setCars([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("cars")
        .select(
          "id, brand, model, variant, year, mileage, price, fuel_type, transmission, body_type, color, doors, seats, engine_size, horsepower, first_registration, is_leasing, leasing_price, status, images, equipment, seo_slug",
        )
        .in("id", idList);
      if (cancelled) return;
      if (error) {
        setCars([]);
        return;
      }
      const byId = new Map<string, CompareCar>((data ?? []).map((c) => [c.id, c as CompareCar]));
      setCars(idList.map((id: string) => byId.get(id)).filter((c): c is CompareCar => !!c));
    })();
    return () => {
      cancelled = true;
    };
  }, [searchIds]);

  if (cars === null) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-16 text-center text-muted-foreground">
        Indlæser sammenligning...
      </div>
    );
  }

  if (cars.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-center">
        <GitCompare size={48} className="mx-auto text-muted-foreground" />
        <h1 className="mt-6 text-3xl font-bold text-foreground">
          Ingen biler at sammenligne
        </h1>
        <p className="mt-3 text-muted-foreground">
          Vælg 2-4 biler fra oversigten og klik på "Sammenlign" for at se deres
          specs side om side.
        </p>
        <Link
          to="/biler-til-salg"
          className="mt-6 inline-flex items-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground transition-colors hover:brightness-110"
        >
          <ArrowLeft size={16} /> Se vores biler
        </Link>
      </div>
    );
  }

  const rows = buildRows(cars);

  const winnerIndex = (row: SpecRow): number | null => {
    if (!row.highlight) return null;
    const numeric = cars.map((c) => {
      switch (row.label) {
        case "Pris":
          return c.price;
        case "Kilometer":
          return c.mileage;
        case "Årgang":
          return c.year;
        case "Hestekræfter":
          return c.horsepower ?? -Infinity;
        default:
          return NaN;
      }
    });
    const valid = numeric.filter((n) => Number.isFinite(n));
    if (valid.length < 2) return null;
    const target =
      row.highlight === "min" ? Math.min(...valid) : Math.max(...valid);
    const winners = numeric
      .map((n, i) => (n === target ? i : -1))
      .filter((i) => i >= 0);
    return winners.length === 1 ? winners[0] : null;
  };

  return (
    <div className="bg-background py-10">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link
              to="/biler-til-salg"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft size={14} /> Tilbage til oversigt
            </Link>
            <h1 className="mt-2 text-3xl font-bold text-foreground sm:text-4xl">
              Sammenlign biler
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {cars.length} {cars.length === 1 ? "bil" : "biler"} side om side
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              clear();
              window.history.pushState({}, "", "/sammenlign");
              setCars([]);
            }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            <X size={14} /> Ryd alle
          </button>
        </div>

        <div className="overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
          <table className="w-full min-w-[640px] border-collapse">
            <thead>
              <tr>
                <th className="sticky left-0 z-10 w-40 border-b border-border bg-card p-4 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:w-48">
                  Bil
                </th>
                {cars.map((c) => {
                  const slug = resolveCarSlug(c);
                  const img = c.images?.[0];
                  return (
                    <th
                      key={c.id}
                      className="min-w-[220px] border-b border-l border-border p-4 align-top"
                    >
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            remove(c.id);
                            const next = idList.filter((x: string) => x !== c.id);
                            window.history.replaceState(
                              {},
                              "",
                              next.length > 0
                                ? `/sammenlign?ids=${next.join(",")}`
                                : "/sammenlign",
                            );
                            setCars((prev) =>
                              (prev ?? []).filter((x) => x.id !== c.id),
                            );
                          }}
                          className="absolute right-0 top-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                          aria-label={`Fjern ${c.brand} ${c.model}`}
                        >
                          <X size={14} />
                        </button>
                        <Link
                          to="/biler-til-salg/$slug"
                          params={{ slug }}
                          className="block"
                        >
                          {img ? (
                            <img
                              src={img}
                              alt={`${c.brand} ${c.model}`}
                              className="aspect-[4/3] w-full rounded-lg object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex aspect-[4/3] w-full items-center justify-center rounded-lg bg-muted">
                              <CarIcon
                                size={32}
                                className="text-muted-foreground"
                              />
                            </div>
                          )}
                          <div className="mt-3 text-left">
                            <div className="text-base font-bold text-foreground">
                              {c.brand} {c.model}
                            </div>
                            {c.variant && (
                              <div className="mt-0.5 text-xs text-muted-foreground">
                                {c.variant}
                              </div>
                            )}
                            <div className="mt-2 text-lg font-semibold text-accent">
                              {fmtPrice(c.price)}
                            </div>
                          </div>
                        </Link>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const winner = winnerIndex(row);
                return (
                  <tr key={row.label} className="even:bg-muted/30">
                    <td className="sticky left-0 z-10 border-b border-border bg-inherit p-4 text-sm font-medium text-muted-foreground">
                      {row.label}
                    </td>
                    {row.values.map((v, i) => (
                      <td
                        key={i}
                        className={`border-b border-l border-border p-4 text-sm ${
                          winner === i
                            ? "font-bold text-accent"
                            : "text-foreground"
                        }`}
                      >
                        {v ?? "—"}
                      </td>
                    ))}
                  </tr>
                );
              })}

              <tr className="even:bg-muted/30">
                <td className="sticky left-0 z-10 border-b border-border bg-inherit p-4 align-top text-sm font-medium text-muted-foreground">
                  Udstyr
                </td>
                {cars.map((c) => (
                  <td
                    key={c.id}
                    className="border-b border-l border-border p-4 align-top text-sm text-foreground"
                  >
                    {c.equipment && c.equipment.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {c.equipment.slice(0, 12).map((e) => (
                          <span
                            key={e}
                            className="rounded-md bg-muted px-2 py-0.5 text-xs"
                          >
                            {e}
                          </span>
                        ))}
                        {c.equipment.length > 12 && (
                          <span className="text-xs text-muted-foreground">
                            +{c.equipment.length - 12} mere
                          </span>
                        )}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-xs text-muted-foreground">
          Fremhævet med guld: laveste pris/km og højeste årgang/hk.
        </p>
      </div>
    </div>
  );
}
